import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, ClipboardCheck, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import AuditPeriodDialog from "@/components/AuditPeriodDialog";
import AuditFormblatt from "@/components/AuditFormblatt";
import ComplaintsRegister from "@/components/ComplaintsRegister";
import { openCertificate } from "@/pages/MyCertificatesPage";

const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmt = (v, unit) => (unit === "€" ? eur(v) : `${Number(v || 0).toLocaleString("de-DE")} ${unit}`);
const VIEWS = [["formblatt", "Formblatt Ziele"], ["kennzahlen", "Kennzahlen"], ["beschwerden", "Beschwerden"]];

function KpiCard({ id, label, k, children }) {
  return (
    <div className="border border-slate-200 bg-white p-5 flex flex-col" data-testid={`audit-kpi-${id}`}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">{label}</div>
      <div className="font-heading text-3xl font-light tracking-tighter text-slate-900" data-testid={`audit-kpi-${id}-actual`}>{fmt(k.actual, k.unit)}</div>
      {children && <div className="text-[11px] text-slate-500 mt-3 space-y-0.5">{children}</div>}
    </div>
  );
}

function KpiGrid({ r }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
      <KpiCard id="avgs-ue" label="AVGS – geleistete UE" k={r.avgs_ue}><div>Unterschriebene Termine im Zeitraum</div></KpiCard>
      <KpiCard id="wb-tn" label="Weiterbildung – Teilnehmer" k={r.wb_tn}><div>Start im Zeitraum</div></KpiCard>
      <KpiCard id="placement" label="Verbleibsquote" k={r.placement}>
        <div>{r.placement.answered} von {r.placement.sent} beantwortet ({r.placement.answer_rate}%)</div>
        <div>Job {r.placement.yes} · Weiterbildung {r.placement.training} · Nichts {r.placement.no}</div>
      </KpiCard>
      <KpiCard id="dropout" label="Abbruchquote" k={r.dropout}><div>AVGS {r.dropout.avgs} · WB {r.dropout.wb} von {r.dropout.total_tn} TN</div></KpiCard>
      <KpiCard id="liquidity" label="Liquidität" k={r.liquidity}>
        <div>Rechnungen ({r.liquidity.invoice_count}): {eur(r.liquidity.invoices)}</div>
        <div>davon bezahlt {eur(r.liquidity.invoices_paid)} · offen {eur(r.liquidity.invoices_open)}</div>
        <div>WB verkauft: {eur(r.liquidity.wb_sold)}</div>
      </KpiCard>
      <KpiCard id="staff-certs" label="Mitarbeiter mit Zertifikat" k={r.staff_certs}>
        <div>{r.staff_certs.staff.filter((s) => s.certs.length).length} von {r.staff_certs.staff.length} Mitarbeitern</div>
      </KpiCard>
      <KpiCard id="wb-survey" label="Zufriedenheit Weiterbildung" k={r.wb_survey}>
        <div>Ø {r.wb_survey.avg?.toLocaleString("de-DE") ?? "—"} / 5 · Rücklauf {r.wb_survey.submitted}/{r.wb_survey.sent} ({r.wb_survey.answer_rate}%)</div>
      </KpiCard>
      <KpiCard id="avgs-survey" label="Zufriedenheit AVGS" k={r.avgs_survey}>
        <div>Ø {r.avgs_survey.avg?.toLocaleString("de-DE") ?? "—"} / 5 · Rücklauf {r.avgs_survey.submitted}/{r.avgs_survey.sent} ({r.avgs_survey.answer_rate}%)</div>
      </KpiCard>
    </div>
  );
}

function DetailLists({ r }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-slate-200 bg-white" data-testid="audit-dropout-list">
        <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Maßnahmeabbrüche</div>
        {r.dropout.list.length === 0 ? <div className="p-5 text-sm text-slate-400">Keine Abbrüche im Zeitraum.</div> : (
          <ul className="divide-y divide-slate-100">{r.dropout.list.map((d, i) => (
            <li key={i} className="px-5 py-3 text-sm">
              <div className="text-slate-900">{d.name} <span className="text-[10px] uppercase text-slate-400 ml-1">{d.kind}</span></div>
              <div className="text-xs text-slate-500">{formatDeShort(d.date)} · {d.reason}</div>
            </li>))}
          </ul>)}
      </div>
      <div className="border border-slate-200 bg-white" data-testid="audit-staff-list">
        <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Mitarbeiter-Zertifikate</div>
        <ul className="divide-y divide-slate-100">{r.staff_certs.staff.map((s) => (
          <li key={s.user_id} className="px-5 py-3 text-sm flex items-start gap-3" data-testid={`audit-staff-${s.user_id}`}>
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${s.certs.length ? "bg-emerald-600" : "bg-red-400"}`} />
            <div className="min-w-0">
              <div className="text-slate-900">{s.name}</div>
              {s.certs.length === 0 ? <div className="text-xs text-slate-400">Kein Zertifikat im Zeitraum</div> : s.certs.map((c) => (
                <button key={c.cert_id} onClick={() => openCertificate(c.cert_id)} className="block text-xs text-slate-600 hover:underline text-left" data-testid={`audit-cert-${c.cert_id}`}>
                  {c.title} · {formatDeShort(c.date)}
                </button>))}
            </div>
          </li>))}
        </ul>
      </div>
    </div>
  );
}

export default function InterneAuditPage() {
  const [periods, setPeriods] = useState([]);
  const [sel, setSel] = useState(null);
  const [report, setReport] = useState(null);
  const [dlg, setDlg] = useState({ open: false, period: null });
  const [view, setView] = useState("formblatt");
  const reload = () => sel && api.get(`/audit/report/${sel}`).then((r) => setReport(r.data));

  const loadPeriods = async (pick) => {
    const { data } = await api.get("/audit/periods");
    setPeriods(data);
    setSel(pick === null ? data[0]?.period_id || null : pick || sel || data[0]?.period_id || null);
  };
  useEffect(() => { loadPeriods(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!sel) return;
    setReport(null);
    api.get(`/audit/report/${sel}`).then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [sel]);

  const current = periods.find((p) => p.period_id === sel);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/audit/report/${sel}/pdf`, { responseType: "blob", timeout: 60000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `Interne_Audit_${(current?.label || "Bericht").replace(/[\s/]+/g, "_")}.pdf`;
      a.click();
    } catch (e) { toast.error("PDF konnte nicht erstellt werden"); }
    finally { setPdfBusy(false); }
  };
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8" data-testid="interne-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 flex items-center gap-1.5"><ClipboardCheck size={12} strokeWidth={1.5} /> AZAV · Qualitätsmanagement</div>
          <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Interne Audit</h1>
          <p className="text-sm text-slate-500 mt-1">FB Unternehmens- und Qualitätsziele je Audit-Zeitraum.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {current && <button onClick={downloadPdf} disabled={pdfBusy} className="px-4 py-2.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5" data-testid="audit-pdf-btn"><Download size={12} strokeWidth={1.5} /> {pdfBusy ? "Erstellt…" : "PDF-Bericht"}</button>}
          {current && <button onClick={() => setDlg({ open: true, period: current })} className="px-4 py-2.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="audit-edit-period-btn"><Pencil size={12} strokeWidth={1.5} /> Ziele bearbeiten</button>}
          <button onClick={() => setDlg({ open: true, period: null })} className="px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5" data-testid="audit-new-period-btn"><Plus size={12} strokeWidth={1.5} /> Neuer Zeitraum</button>
        </div>
      </div>
      {periods.length > 0 && (
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="audit-period-tabs">
          {periods.map((p) => (
            <button key={p.period_id} onClick={() => setSel(p.period_id)} data-testid={`audit-period-tab-${p.period_id}`}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap border shrink-0 ${sel === p.period_id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
              {p.label} · {formatDeShort(p.start)}–{formatDeShort(p.end)}
            </button>))}
        </div>
      )}
      {periods.length === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="audit-empty">Legen Sie zuerst einen Audit-Zeitraum mit Ihren Zielen an.</div>
      ) : (
        <>
          <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="audit-view-tabs">
            {VIEWS.map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`audit-view-${k}`}
                className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${view === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          {view === "beschwerden" ? <ComplaintsRegister onChanged={reload} /> : !report ? <div className="text-xs text-slate-400">Lädt…</div>
            : view === "formblatt" ? <AuditFormblatt r={report} onChanged={reload} /> : (<><KpiGrid r={report} /><DetailLists r={report} /></>)}
        </>
      )}
      <AuditPeriodDialog open={dlg.open} period={dlg.period} onClose={() => setDlg({ open: false, period: null })}
        onSaved={(id) => { setDlg({ open: false, period: null }); setReport(null); loadPeriods(id); if (id === sel) api.get(`/audit/report/${id}`).then((r) => setReport(r.data)); }} />
    </div>
  );
}
